'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { motion, useMotionValue, useTransform, animate } from 'framer-motion'
import {
  TrendingUp,
  DollarSign,
  PiggyBank,
  Info,
  ChevronDown,
} from 'lucide-react'
import {
  projectTsp,
  type TspInput,
  type TspResult,
  type TspFundAllocation,
} from '@/app/actions'

/* ── Constants ─────────────────────────────────────────── */
const RANKS = [
  'E-1','E-2','E-3','E-4','E-5','E-6','E-7','E-8','E-9',
  'W-1','W-2','W-3','W-4','W-5',
  'O-1','O-2','O-3','O-4','O-5','O-6','O-7','O-8','O-9','O-10',
]

const YOS_OPTIONS = Array.from({ length: 31 }, (_, i) => i) // 0–30

const FUND_COLORS: Record<keyof TspFundAllocation, string> = {
  G: '#6b7280', // gray
  F: '#3b82f6', // blue
  C: '#22c55e', // green
  S: '#f59e0b', // amber
  I: '#a855f7', // purple
}

const FUND_LABELS: Record<keyof TspFundAllocation, string> = {
  G: 'G Fund (Gov\'t Securities)',
  F: 'F Fund (Fixed Income)',
  C: 'C Fund (S&P 500)',
  S: 'S Fund (Small Cap)',
  I: 'I Fund (International)',
}

const FUND_PRESETS: { label: string; allocation: TspFundAllocation }[] = [
  { label: 'Aggressive', allocation: { G: 0, F: 0, C: 60, S: 30, I: 10 } },
  { label: 'Moderate', allocation: { G: 10, F: 10, C: 40, S: 25, I: 15 } },
  { label: 'Conservative', allocation: { G: 40, F: 30, C: 20, S: 5, I: 5 } },
  { label: 'All C Fund', allocation: { G: 0, F: 0, C: 100, S: 0, I: 0 } },
  { label: 'Lifecycle-ish', allocation: { G: 10, F: 10, C: 50, S: 20, I: 10 } },
]

/* ── Animated number ──────────────────────────────────── */
function AnimatedNumber({ value, prefix = '$' }: { value: number; prefix?: string }) {
  const mv = useMotionValue(0)
  const display = useTransform(mv, (v) =>
    `${prefix}${Math.round(v).toLocaleString()}`
  )
  const [text, setText] = useState(`${prefix}0`)

  useEffect(() => {
    const unsub = display.on('change', (v) => setText(v))
    return unsub
  }, [display])

  useEffect(() => {
    const ctrl = animate(mv, value, { duration: 1.2, ease: [0.16, 1, 0.3, 1] })
    return () => ctrl.stop()
  }, [value, mv])

  return <span>{text}</span>
}

/* ── Growth chart (SVG area) ──────────────────────────── */
function GrowthChart({ result }: { result: TspResult }) {
  const { years } = result
  if (years.length === 0) return null

  const W = 700
  const H = 300
  const PAD = { top: 20, right: 20, bottom: 40, left: 70 }
  const cw = W - PAD.left - PAD.right
  const ch = H - PAD.top - PAD.bottom

  const maxBal = Math.max(...years.map((y) => y.endBalance), 1)
  const x = (i: number) => PAD.left + (i / (years.length - 1 || 1)) * cw
  const y = (val: number) => PAD.top + ch - (val / maxBal) * ch

  // Build stacked areas: contributions, match, growth
  const contribs = years.map((yr, i) => {
    let cTotal = 0
    for (let j = 0; j <= i; j++) cTotal += years[j].memberContribution
    return cTotal
  })
  const matches = years.map((yr, i) => {
    let mTotal = 0
    for (let j = 0; j <= i; j++) mTotal += years[j].govMatch
    return contribs[i] + mTotal
  })
  const totals = years.map((yr) => yr.endBalance)

  // Build path strings
  const areaPath = (vals: number[]) => {
    const forward = vals.map((v, i) => `${x(i)},${y(v)}`).join(' L')
    const backward = vals.map((_, i) => `${x(vals.length - 1 - i)},${y(0)}`).join(' L')
    return `M${forward} L${backward} Z`
  }

  const linePath = (vals: number[]) =>
    vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(v)}`).join(' ')

  // Y-axis labels
  const yTicks = 5
  const yLabels = Array.from({ length: yTicks + 1 }, (_, i) => Math.round((maxBal / yTicks) * i))

  // X-axis labels (every 5 years)
  const xLabels = years.filter((yr) => yr.year % 5 === 0 || yr.year === years.length)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      {/* Grid lines */}
      {yLabels.map((val) => (
        <g key={val}>
          <line
            x1={PAD.left} x2={W - PAD.right}
            y1={y(val)} y2={y(val)}
            stroke="var(--border)" strokeWidth={0.5} strokeDasharray="4 4"
          />
          <text x={PAD.left - 8} y={y(val) + 3} textAnchor="end" fill="var(--text-3)" fontSize={10}>
            ${(val / 1000).toFixed(0)}k
          </text>
        </g>
      ))}

      {/* Stacked areas */}
      <path d={areaPath(totals)} fill="#10b981" opacity={0.15} />
      <path d={areaPath(matches)} fill="#3b82f6" opacity={0.2} />
      <path d={areaPath(contribs)} fill="#1e3a8a" opacity={0.2} />

      {/* Lines */}
      <path d={linePath(totals)} fill="none" stroke="#10b981" strokeWidth={2.5} />
      <path d={linePath(matches)} fill="none" stroke="#3b82f6" strokeWidth={1.5} strokeDasharray="4 3" />
      <path d={linePath(contribs)} fill="none" stroke="#1e3a8a" strokeWidth={1.5} strokeDasharray="4 3" />

      {/* End dot */}
      <circle cx={x(years.length - 1)} cy={y(totals[totals.length - 1])} r={4} fill="#10b981" />

      {/* X axis labels */}
      {xLabels.map((yr) => (
        <text
          key={yr.year}
          x={x(yr.year - 1)}
          y={H - 8}
          textAnchor="middle"
          fill="var(--text-3)"
          fontSize={10}
        >
          Yr {yr.year}
        </text>
      ))}

      {/* Legend */}
      <g transform={`translate(${PAD.left + 8}, ${PAD.top + 8})`}>
        {[
          { color: '#1e3a8a', label: 'Your Contributions' },
          { color: '#3b82f6', label: 'Gov Match' },
          { color: '#10b981', label: 'Total Balance' },
        ].map((item, i) => (
          <g key={item.label} transform={`translate(${i * 140}, 0)`}>
            <rect width={10} height={10} rx={2} fill={item.color} opacity={0.8} />
            <text x={14} y={9} fill="var(--text-3)" fontSize={9}>{item.label}</text>
          </g>
        ))}
      </g>
    </svg>
  )
}

/* ── Fund allocation bar ──────────────────────────────── */
function AllocationBar({ allocation }: { allocation: TspFundAllocation }) {
  const total = allocation.G + allocation.F + allocation.C + allocation.S + allocation.I
  if (total === 0) return null

  return (
    <div className="flex h-3 overflow-hidden rounded-full">
      {(Object.keys(FUND_COLORS) as (keyof TspFundAllocation)[]).map((fund) => {
        const pct = (allocation[fund] / total) * 100
        if (pct === 0) return null
        return (
          <div
            key={fund}
            style={{ width: `${pct}%`, backgroundColor: FUND_COLORS[fund] }}
            className="transition-all duration-300"
            title={`${fund}: ${allocation[fund]}%`}
          />
        )
      })}
    </div>
  )
}

/* ── Breakdown table ──────────────────────────────────── */
function BreakdownTable({ result }: { result: TspResult }) {
  const [expanded, setExpanded] = useState(false)
  const { years } = result
  const shown = expanded ? years : years.slice(0, 10)

  const fmt = (n: number) => `$${n.toLocaleString()}`

  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[var(--border)] bg-[var(--surface-2)]">
              {['Year', 'Base Pay', 'Your Contribution', 'Gov Match', 'Growth', 'Balance'].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-[var(--text-3)]">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((yr) => (
              <tr key={yr.year} className="border-t border-[var(--border)]/50 transition hover:bg-[var(--surface-2)]/50">
                <td className="px-4 py-2.5 font-bold text-[var(--text-1)]">{yr.year}</td>
                <td className="px-4 py-2.5 text-[var(--text-2)]">{fmt(yr.basePay)}</td>
                <td className="px-4 py-2.5 font-semibold text-[var(--accent)]">{fmt(yr.memberContribution)}</td>
                <td className="px-4 py-2.5 font-semibold text-blue-600">{fmt(yr.govMatch)}</td>
                <td className="px-4 py-2.5 font-semibold text-emerald-500">{fmt(yr.growth)}</td>
                <td className="px-4 py-2.5 font-bold text-[var(--text-1)]">{fmt(yr.endBalance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {years.length > 10 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full border-t border-[var(--border)] py-2.5 text-xs font-semibold text-[var(--text-3)] hover:text-[var(--text-1)] transition"
        >
          {expanded ? 'Show Less' : `Show All ${years.length} Years`}
        </button>
      )}
    </div>
  )
}

/* ── Main Component ───────────────────────────────────── */
export default function TSPProjector() {
  // Inputs
  const [rank, setRank] = useState('E-4')
  const [yos, setYos] = useState(2)
  const [contributionPct, setContributionPct] = useState(5)
  const [startingBalance, setStartingBalance] = useState(0)
  const [projectionYears, setProjectionYears] = useState(20)
  const [annualRaise, setAnnualRaise] = useState(2.5)
  const [useBrsMatch, setUseBrsMatch] = useState(true)
  const [allocation, setAllocation] = useState<TspFundAllocation>(
    FUND_PRESETS[0].allocation
  )
  const [activePreset, setActivePreset] = useState<string>('Aggressive')

  // Result
  const [result, setResult] = useState<TspResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateFund = useCallback((fund: keyof TspFundAllocation, val: number) => {
    setAllocation((prev) => ({ ...prev, [fund]: val }))
    setActivePreset('')
  }, [])

  const applyPreset = useCallback((preset: typeof FUND_PRESETS[number]) => {
    setAllocation(preset.allocation)
    setActivePreset(preset.label)
  }, [])

  const allocTotal = useMemo(
    () => allocation.G + allocation.F + allocation.C + allocation.S + allocation.I,
    [allocation]
  )

  const run = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await projectTsp({
        rank,
        yearsOfService: yos,
        contributionPct,
        startingBalance,
        projectionYears,
        annualRaise,
        useBrsMatch,
        fundAllocation: allocation,
      })
      setResult(res)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }, [rank, yos, contributionPct, startingBalance, projectionYears, annualRaise, useBrsMatch, allocation])

  // Auto-run on first load and when inputs change
  useEffect(() => {
    const timer = setTimeout(run, 400)
    return () => clearTimeout(timer)
  }, [run])

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600/10">
            <PiggyBank className="h-5 w-5 text-[var(--accent)]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--text-1)]">TSP Projector</h1>
            <p className="text-sm text-[var(--text-3)]">Forecast your Thrift Savings Plan growth</p>
          </div>
        </div>
      </div>

      {/* ── Input form ─────────────────────────────────────── */}
      <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm space-y-6">
        {/* Row 1: Rank, YOS, Contribution % */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-3)]">Rank</label>
            <select
              value={rank}
              onChange={(e) => setRank(e.target.value)}
              className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm font-semibold text-[var(--text-1)] outline-none focus:ring-2 focus:ring-blue-600/40"
            >
              {RANKS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-3)]">Years of Service</label>
            <select
              value={yos}
              onChange={(e) => setYos(Number(e.target.value))}
              className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm font-semibold text-[var(--text-1)] outline-none focus:ring-2 focus:ring-blue-600/40"
            >
              {YOS_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-3)]">Contribution %</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={0}
                max={100}
                value={contributionPct}
                onChange={(e) => setContributionPct(Number(e.target.value))}
                className="flex-1 accent-blue-600"
              />
              <span className="w-10 text-right text-sm font-bold text-[var(--text-1)]">{contributionPct}%</span>
            </div>
          </div>
        </div>

        {/* Row 2: Starting balance, Projection years, Annual raise */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-3)]">Starting Balance</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--text-3)]">$</span>
              <input
                type="number"
                value={startingBalance || ''}
                onChange={(e) => setStartingBalance(Number(e.target.value) || 0)}
                placeholder="0"
                className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface-2)] pl-7 pr-3 py-2 text-sm font-semibold text-[var(--text-1)] outline-none focus:ring-2 focus:ring-blue-600/40"
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-3)]">Projection Years</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={1}
                max={40}
                value={projectionYears}
                onChange={(e) => setProjectionYears(Number(e.target.value))}
                className="flex-1 accent-blue-600"
              />
              <span className="w-10 text-right text-sm font-bold text-[var(--text-1)]">{projectionYears}yr</span>
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-3)]">Annual Pay Raise</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={0}
                max={6}
                step={0.5}
                value={annualRaise}
                onChange={(e) => setAnnualRaise(Number(e.target.value))}
                className="flex-1 accent-blue-600"
              />
              <span className="w-10 text-right text-sm font-bold text-[var(--text-1)]">{annualRaise}%</span>
            </div>
          </div>
        </div>

        {/* BRS Match toggle */}
        <div className="flex items-center justify-between rounded-xl bg-[var(--surface-2)] px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-[var(--text-1)]">BRS Government Match</p>
            <p className="text-xs text-[var(--text-3)]">1% automatic + up to 4% matching (5% total)</p>
          </div>
          <div className="flex overflow-hidden rounded-lg border border-[var(--border)]">
            {[true, false].map((val) => (
              <button
                key={String(val)}
                onClick={() => setUseBrsMatch(val)}
                className={`px-4 py-1.5 text-xs font-semibold transition ${
                  useBrsMatch === val
                    ? 'bg-blue-600 text-white'
                    : 'bg-[var(--surface-1)] text-[var(--text-3)] hover:text-[var(--text-1)]'
                }`}
              >
                {val ? 'Yes' : 'No'}
              </button>
            ))}
          </div>
        </div>

        {/* Fund allocation */}
        <div>
          <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-3)]">Fund Allocation</label>

          {/* Presets */}
          <div className="mb-3 flex flex-wrap gap-2">
            {FUND_PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => applyPreset(preset)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                  activePreset === preset.label
                    ? 'bg-blue-600 text-white'
                    : 'bg-[var(--surface-2)] text-[var(--text-3)] hover:text-[var(--text-1)]'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Allocation bar */}
          <AllocationBar allocation={allocation} />

          {/* Fund sliders */}
          <div className="mt-3 grid grid-cols-5 gap-3">
            {(Object.keys(FUND_COLORS) as (keyof TspFundAllocation)[]).map((fund) => (
              <div key={fund}>
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: FUND_COLORS[fund] }} />
                  <span className="text-[10px] font-bold text-[var(--text-2)]">{fund}</span>
                  <span className="ml-auto text-[10px] font-bold text-[var(--text-1)]">{allocation[fund]}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={allocation[fund]}
                  onChange={(e) => updateFund(fund, Number(e.target.value))}
                  className="w-full accent-current"
                  style={{ color: FUND_COLORS[fund] }}
                />
              </div>
            ))}
          </div>

          {allocTotal !== 100 && (
            <p className="mt-2 text-xs font-semibold text-red-500">
              Allocation totals {allocTotal}% — should equal 100%
            </p>
          )}
        </div>
      </div>

      {/* ── Results ────────────────────────────────────────── */}
      {error && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 px-5 py-3 text-sm text-red-400">
          {error}
        </div>
      )}

      {result && !error && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-6"
        >
          {/* Hero stats */}
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: 'Final Balance', value: result.finalBalance, color: 'text-emerald-500', icon: TrendingUp },
              { label: 'You Contributed', value: result.totalContributed, color: 'text-[var(--accent)]', icon: DollarSign },
              { label: 'Gov Match', value: result.totalMatch, color: 'text-blue-600', icon: PiggyBank },
              { label: 'Investment Growth', value: result.totalGrowth, color: 'text-emerald-500', icon: TrendingUp },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-5 shadow-sm"
              >
                <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600/10">
                  <stat.icon className={`h-4 w-4 ${stat.color}`} />
                </div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-3)]">{stat.label}</p>
                <p className={`mt-1 text-xl font-bold ${stat.color}`}>
                  <AnimatedNumber value={stat.value} />
                </p>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
            <h2 className="mb-4 text-sm font-bold text-[var(--text-1)]">Growth Over Time</h2>
            <GrowthChart result={result} />
          </div>

          {/* Year-by-year table */}
          <div>
            <h2 className="mb-3 text-sm font-bold text-[var(--text-1)]">Year-by-Year Breakdown</h2>
            <BreakdownTable result={result} />
          </div>

          {/* Free money callout */}
          {useBrsMatch && result.totalMatch > 0 && (
            <div className="flex items-start gap-3 rounded-xl border border-blue-600/20 bg-blue-600/5 px-5 py-4">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
              <div>
                <p className="text-sm font-semibold text-blue-600">
                  ${result.totalMatch.toLocaleString()} in free government match
                </p>
                <p className="mt-0.5 text-xs text-[var(--text-3)]">
                  This is money the government contributes to your TSP on top of your own contributions.
                  To maximize it, contribute at least 5% of your base pay.
                </p>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {loading && !result && (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        </div>
      )}
    </div>
  )
}
